"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PermissionGate = PermissionGate;
exports.AdminOnly = AdminOnly;
exports.SupervisorOnly = SupervisorOnly;
var jsx_runtime_1 = require("react/jsx-runtime");
var use_permissions_1 = require("../../hooks/use-permissions");
/**
 * Permission gate component that conditionally renders children based on user permissions.
 *
 * @example
 * // Single permission check
 * <PermissionGate module="users" action="create">
 *   <CreateUserButton />
 * </PermissionGate>
 *
 * @example
 * // Module access check (any action)
 * <PermissionGate module="orders">
 *   <OrdersLink />
 * </PermissionGate>
 *
 * @example
 * // Multiple permissions (all required)
 * <PermissionGate
 *   module="orders"
 *   requireAll
 *   permissions={[
 *     { module: "orders", action: "view" },
 *     { module: "orders", action: "authorize" }
 *   ]}
 * >
 *   <AuthorizeButton />
 * </PermissionGate>
 */
function PermissionGate(props) {
    var children = props.children, module = props.module, action = props.action, _a = props.fallback, fallback = _a === void 0 ? null : _a, _b = props.requireAll, requireAll = _b === void 0 ? false : _b, permissions = props.permissions;
    var _c = (0, use_permissions_1.usePermissions)(), hasPermission = _c.hasPermission, canAccess = _c.canAccess;
    // Check multiple permissions
    if (permissions && permissions.length > 0) {
        var results = permissions.map(function (p) { return hasPermission(p.module, p.action); });
        if (requireAll) {
            // All permissions must be true
            if (!results.every(function (r) { return r; })) {
                return (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: fallback });
            }
        }
        else {
            // At least one permission must be true
            if (!results.some(function (r) { return r; })) {
                return (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: fallback });
            }
        }
        return (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: children });
    }
    // Single permission check
    if (action) {
        if (!hasPermission(module, action)) {
            return (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: fallback });
        }
        return (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: children });
    }
    // Module access check (any action)
    if (!canAccess(module)) {
        return (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: fallback });
    }
    return (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: children });
}
/**
 * Admin-only component wrapper.
 *
 * @example
 * <AdminOnly>
 *   <SystemSettings />
 * </AdminOnly>
 */
function AdminOnly(props) {
    var children = props.children, _a = props.fallback, fallback = _a === void 0 ? null : _a;
    var isAdmin = (0, use_permissions_1.usePermissions)().isAdmin;
    if (!isAdmin()) {
        return (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: fallback });
    }
    return (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: children });
}
/**
 * Supervisor or admin only component wrapper.
 *
 * @example
 * <SupervisorOnly>
 *   <ApprovalActions />
 * </SupervisorOnly>
 */
function SupervisorOnly(props) {
    var children = props.children, _a = props.fallback, fallback = _a === void 0 ? null : _a;
    var isSupervisor = (0, use_permissions_1.usePermissions)().isSupervisor;
    if (!isSupervisor()) {
        return (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: fallback });
    }
    return (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: children });
}
